import type { Locale } from "../i18n/locale";
import { numberLocaleFor } from "../i18n/locale";
import type { UiKey } from "../i18n/ui";
import type { AudienceMode } from "./audienceMode";
import type { AwsRegionId } from "./awsRegion";
import type { DeploymentStackConfig } from "./deploymentStack";
import type { DisplayCurrency } from "./displayCurrency";
import type { ComputeProfileId } from "./computeProfile";
import type { LlmPriceProfile } from "./llmPriceProfile";
import {
  COEFFICIENTS_LAST_UPDATED,
  MODEL_VERSION,
  REFERENCE_LINKS,
  SCHEMA_VERSION,
} from "./modelMeta";
import type { SavingsLevers } from "./savingsLevers";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type ReportAuditContext = {
  region: AwsRegionId;
  savings: SavingsLevers;
  deploymentStack: DeploymentStackConfig;
  llmProfile: LlmPriceProfile;
  computeProfile: ComputeProfileId;
  audienceMode: AudienceMode;
  budgetCapUsd: number | null;
  scenarioNotes?: string;
  displayCurrency: DisplayCurrency;
};

function savingsLines(s: SavingsLevers, t: (k: UiKey) => string): string[] {
  const rows: { on: boolean; label: UiKey }[] = [
    { on: s.spotOrPreemptibleCompute, label: "savings.spot" },
    { on: s.dynamoCommittedCapacity, label: "savings.dynamo" },
    { on: s.llmEconomyTier, label: "savings.llm" },
    { on: s.llmBatchApi, label: "savings.llmBatch" },
    { on: s.scheduleOffWeekends, label: "savings.weekends" },
  ];
  return rows.filter((r) => r.on).map((r) => t(r.label));
}

function stackLines(stack: DeploymentStackConfig, t: (k: UiKey) => string): string[] {
  const fe =
    stack.frontend === "cloudfront"
      ? t("stack.frontend.cloudfront")
      : stack.frontend === "ec2_spa"
        ? t("stack.frontend.ec2_spa")
        : t("stack.frontend.apigw");
  const be =
    stack.backend === "ec2"
      ? t("stack.backend.ec2")
      : stack.backend === "eks"
        ? t("stack.backend.eks")
        : t("stack.backend.lambda");
  const db =
    stack.database === "rds"
      ? t("stack.database.rds")
      : stack.database === "dynamo_and_rds"
        ? t("stack.database.both")
        : t("stack.database.dynamo");
  const net =
    stack.networkExposure === "private"
      ? t("stack.network.private")
      : t("stack.network.public");
  const extras: string[] = [];
  if (stack.route53Enabled) extras.push(t("stack.route53"));
  if (stack.albWafEnabled) extras.push(t("stack.albWaf"));
  if (stack.guardDutyEnabled) extras.push(t("stack.guardDuty"));
  return [
    `${t("stack.frontend")}: ${fe}`,
    `${t("stack.backend")}: ${be}`,
    `${t("stack.database")}: ${db}`,
    `${t("stack.network")}: ${net}`,
    extras.length ? `${t("stack.extrasTitle")}: ${extras.join("; ")}` : "",
  ].filter(Boolean);
}

function llmLabel(p: LlmPriceProfile, t: (k: UiKey) => string): string {
  if (p === "economy") return t("llmProfile.economy");
  if (p === "premium") return t("llmProfile.premium");
  return t("llmProfile.standard");
}

function computeProfileLabel(p: ComputeProfileId, t: (k: UiKey) => string): string {
  return p === "gpu" ? t("computeProfile.gpu") : t("computeProfile.cpu");
}

function audienceLabel(m: AudienceMode, t: (k: UiKey) => string): string {
  return m === "technical" ? t("audience.technical") : t("audience.simple");
}

function formatBudget(
  locale: Locale,
  cap: number | null,
  t: (k: UiKey) => string,
): string {
  if (cap == null) return t("export.appendix.budgetNone");
  const nf = numberLocaleFor(locale);
  return cap.toLocaleString(nf, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Markdown block inserted before the legal footer in exports. */
export function buildAuditAppendixMarkdown(
  locale: Locale,
  t: (k: UiKey) => string,
  ctx: ReportAuditContext,
): string {
  const lines: string[] = [
    `## ${t("export.appendix.trustTitle")}`,
    "",
    `- **${t("export.appendix.modelVersion")}** \`${MODEL_VERSION}\``,
    `- **${t("export.appendix.schemaVersion")}** \`${SCHEMA_VERSION}\``,
    `- **${t("export.appendix.coefficientsDate")}** ${COEFFICIENTS_LAST_UPDATED}`,
    "",
    `### ${t("export.appendix.referencesTitle")}`,
    "",
  ];
  for (const ref of REFERENCE_LINKS) {
    lines.push(`- [${t(ref.labelKey)}](${ref.href})`);
  }
  lines.push(
    "",
    `### ${t("export.appendix.snapshotTitle")}`,
    "",
    `- **${t("export.appendix.region")}** \`${ctx.region}\``,
    `- **${t("export.appendix.audience")}** ${audienceLabel(ctx.audienceMode, t)}`,
    `- **${t("export.appendix.llmProfile")}** ${llmLabel(ctx.llmProfile, t)}`,
    `- **${t("export.appendix.computeProfile")}** ${computeProfileLabel(ctx.computeProfile, t)}`,
    `- **${t("export.appendix.displayCurrency")}** ${ctx.displayCurrency}`,
    `- **${t("export.appendix.budgetCap")}** ${formatBudget(locale, ctx.budgetCapUsd, t)}`,
    "",
    `**${t("export.appendix.savingsOn")}**`,
  );
  const sav = savingsLines(ctx.savings, t);
  if (sav.length) for (const x of sav) lines.push(`- ${x}`);
  else lines.push(`- ${t("export.appendix.savingsNone")}`);
  lines.push("", `**${t("export.appendix.stackTitle")}**`, "");
  for (const x of stackLines(ctx.deploymentStack, t)) lines.push(`- ${x}`);
  if (ctx.scenarioNotes?.trim()) {
    lines.push(
      "",
      `### ${t("export.appendix.notesTitle")}`,
      "",
      ctx.scenarioNotes.trim(),
    );
  }
  lines.push("", `*${t("export.appendix.disclaimer")}*`);
  return lines.join("\n");
}

/** HTML fragment for the same audit block (embedded in full report HTML). */
export function buildAuditAppendixHtml(
  locale: Locale,
  t: (k: UiKey) => string,
  ctx: ReportAuditContext,
): string {
  const refs = REFERENCE_LINKS.map(
    (r) =>
      `<li><a href="${esc(r.href)}" rel="noopener noreferrer">${esc(t(r.labelKey))}</a></li>`,
  ).join("");
  const sav = savingsLines(ctx.savings, t);
  const savHtml = sav.length
    ? `<ul>${sav.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>`
    : `<p>${esc(t("export.appendix.savingsNone"))}</p>`;
  const stackHtml = `<ul>${stackLines(ctx.deploymentStack, t)
    .map((x) => `<li>${esc(x)}</li>`)
    .join("")}</ul>`;
  const notes = ctx.scenarioNotes?.trim()
    ? `<h3>${esc(t("export.appendix.notesTitle"))}</h3><pre class="notes">${esc(ctx.scenarioNotes.trim())}</pre>`
    : "";
  return `<section class="audit-appendix">
<h2>${esc(t("export.appendix.trustTitle"))}</h2>
<ul>
<li><strong>${esc(t("export.appendix.modelVersion"))}</strong> <code>${esc(MODEL_VERSION)}</code></li>
<li><strong>${esc(t("export.appendix.schemaVersion"))}</strong> <code>${esc(SCHEMA_VERSION)}</code></li>
<li><strong>${esc(t("export.appendix.coefficientsDate"))}</strong> ${esc(COEFFICIENTS_LAST_UPDATED)}</li>
</ul>
<h3>${esc(t("export.appendix.referencesTitle"))}</h3>
<ul>${refs}</ul>
<h3>${esc(t("export.appendix.snapshotTitle"))}</h3>
<ul>
<li><strong>${esc(t("export.appendix.region"))}</strong> <code>${esc(ctx.region)}</code></li>
<li><strong>${esc(t("export.appendix.audience"))}</strong> ${esc(audienceLabel(ctx.audienceMode, t))}</li>
<li><strong>${esc(t("export.appendix.llmProfile"))}</strong> ${esc(llmLabel(ctx.llmProfile, t))}</li>
<li><strong>${esc(t("export.appendix.computeProfile"))}</strong> ${esc(computeProfileLabel(ctx.computeProfile, t))}</li>
<li><strong>${esc(t("export.appendix.displayCurrency"))}</strong> ${esc(ctx.displayCurrency)}</li>
<li><strong>${esc(t("export.appendix.budgetCap"))}</strong> ${esc(formatBudget(locale, ctx.budgetCapUsd, t))}</li>
</ul>
<h3>${esc(t("export.appendix.savingsOn"))}</h3>
${savHtml}
<h3>${esc(t("export.appendix.stackTitle"))}</h3>
${stackHtml}
${notes}
<p class="audit-foot">${esc(t("export.appendix.disclaimer"))}</p>
</section>`;
}

export function buildReadonlyHtmlBanner(t: (k: UiKey) => string): string {
  return `<div class="readonly-share-banner">${esc(t("export.readonlyBanner"))}</div>`;
}
